import { Button, Dialog } from "@mui/material";
import { useEffect, useState } from "react";
import { put_ReservationExtension } from "../../services/Extension";
import useAuth from "../hooks/useAuth";
import { branchTimezone } from "../../utils/summaryCalculation";
import { useTheme } from "../hooks/useTheme";
import colorConfigs from "../../configs/colorConfigs";
import Cookies from "js-cookie";
import useTenant from "../hooks/useTenant";
import moment from "moment";
import useGeneral from "../hooks/useGeneral";

type EditExtensionModalProps = {
    editExtensionIsOpen: boolean;
    setEditExtensionIsOpen: (value: boolean) => void;
    modalData: any,
    setExtensionRefresh: (value: boolean) => void;
    formattedCreatedAt: string;
    isCompletedOrCancelled: boolean;
    setPleaseWait: (value: boolean) => void;
}

const EditExtensionModal = ({
    editExtensionIsOpen,
    setEditExtensionIsOpen,
    modalData,
    setExtensionRefresh,
    formattedCreatedAt,
    isCompletedOrCancelled,
    setPleaseWait
}: EditExtensionModalProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { theme, themeStyles } = useTheme();
    const { dateFormat }: any = useGeneral();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const [oldReturnDate, setOldReturnDate] = useState<string>("");
    const [newReturnDate, setNewReturnDate] = useState<string>("");
    const [comment, setComment] = useState<string>("");
    const [createdBy, setCreatedBy] = useState<string>("");

    const UpdateTimezone = branchTimezone(formattedCreatedAt);
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    useEffect(() => {
        if (editExtensionIsOpen && modalData) {
            setNewReturnDate(moment(new Date(modalData?.newReturenDate)).format(dateFormat + " hh:mm a"));
            setOldReturnDate(moment(new Date(modalData?.oldReturenDate)).format(dateFormat + " hh:mm a"));
            setComment(modalData?.comment);
            setCreatedBy(modalData?.createdBy);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalData, editExtensionIsOpen])

    const handleSubmit = () => {
        setPleaseWait(true)

        put_ReservationExtension({
            ...modalData,
            comment: comment,
            updateBy: auth?.givenname,
            updateAt: new Date(UpdateTimezone)?.toLocaleString()
        }, auth?.id, tenantId)
            .then(() => {
                setPleaseWait(false)
                setEditExtensionIsOpen(false);
                setExtensionRefresh(true);
                resetTheForm();
                handleTenantError(null);
            })
            .catch((error) => {
                setPleaseWait(false)
                handleTenantError(error);
            })
    }

    const resetTheForm = () => {
        setOldReturnDate("");
        setNewReturnDate("");
        setComment("");
        setCreatedBy("");
    }


    return (
        <>
            <Dialog
                open={editExtensionIsOpen}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '500px',
                        width: '100%',
                        borderRadius: '16px',
                        overflowY: "unset",
                    },
                }}
            >
                <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320.591 320.591"
                        className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                        onClick={() => setEditExtensionIsOpen(false)}
                    >
                        <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                        <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                    </svg>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Edit Extension</h2>
                                    </div>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <div>
                                            <div className="grid grid-cols-2 gap-6">
                                                <div className="space-y-4">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>New Return Date</label>
                                                        <br />
                                                        <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{newReturnDate}</span>
                                                    </div>

                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Created By</label>
                                                        <br />
                                                        <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{createdBy}</span>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div>
                                                        <label className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Old Return Date</label>
                                                        <br />
                                                        <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{oldReturnDate}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="w-full mt-4">
                                                <label className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Comments</label>
                                                <textarea
                                                    className={`w-full px-[8px] py-[8px]  border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    rows={6}
                                                    value={comment}
                                                    onChange={(e) => setComment(e.target.value)}
                                                    required
                                                />
                                            </div>

                                            <div className="mt-4 w-full flex gap-4">
                                                {
                                                    !isCompletedOrCancelled && (
                                                        <Button variant="contained"
                                                            sx={{
                                                                backgroundColor: colorConfigs.btn.main.bg
                                                            }}
                                                            onClick={() => handleSubmit()}
                                                        >
                                                            Save
                                                        </Button>

                                                    )
                                                }

                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={() => setEditExtensionIsOpen(false)}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default EditExtensionModal;